import type { KanbanComment } from '@ts-kanban-comments/interfaces'

import { useKanbanCommentsStore } from '@stores/kanban-comments'
import { useCommonStore } from '@stores/common'

import { useAdminRoutes } from '@composables/routes/admin-route.hook'

import { getAxiosErrorMessage } from '@helpers/axios'

import { useToast } from '@UI/Toast/ToastProvider'

interface DisciplineTaskCommentsApiOptions {
  customerId: string | number
  disciplineId?: (string | number)[]
  createdFrom?: string | null
  createdTo?: string | null
  onStart?: () => void
  onSuccess?: (data: KanbanComment[]) => void
  onError?: (err: unknown) => void
  onFinish?: () => void
}

const buildApiParams = (options: DisciplineTaskCommentsApiOptions): Record<string, string | number | (string | number)[]> => {
  const apiParams: Record<string, string | number | (string | number)[]> = {
    customer_id: options.customerId,
  }

  if (options.disciplineId?.length) apiParams.discipline_id = options.disciplineId
  if (options.createdFrom) apiParams.created_from = options.createdFrom
  if (options.createdTo) apiParams.created_to = options.createdTo

  return apiParams
}

export const useDisciplineTaskCommentsApi = async (options: DisciplineTaskCommentsApiOptions): Promise<KanbanComment[]> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const url = useAdminRoutes('admin.kanban-tasks-comments', buildApiParams(options))

  const kanbanCommentsStore = useKanbanCommentsStore()
  const commonStore = useCommonStore()

  options.onStart?.()
  commonStore.setLoading(true)

  try {
    const { data } = await $axios.get(url!)
    const comments: KanbanComment[] = data.data

    kanbanCommentsStore.setComments(comments)
    kanbanCommentsStore.setCustomerId(options.customerId)
    commonStore.setLoading(false)

    options.onSuccess?.(comments)

    return comments
  } catch (err: unknown) {
    console.error(err)
    toast.error(getAxiosErrorMessage(err))
    commonStore.setLoading(false)
    options.onError?.(err)

    return []
  } finally {
    options.onFinish?.()
  }
}